import * as React from "react";
import { Card } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { Pill } from "@/components/ui/pill";
import { Button } from "@/components/ui/button";
import { useActiveRun } from "@/features/production/hooks";
import { OperationalEntryDisclosure } from "@/features/lines/operational-entry/OperationalEntryDisclosure";
import { EditPlannedTimesDialog } from "@/features/production/EditPlannedTimesDialog";
import { PlanDatesGrid } from "./PlanDatesGrid";
import { BatchStrip } from "@/features/batches/BatchStrip";
import { useAuthStore } from "@/stores/auth.store";
import { formatRelativeTime, getQuantityLabel } from "@/lib/display-labels";

export interface ActiveRunCardProps {
  lineId: string;
}

export function ActiveRunCard({ lineId }: ActiveRunCardProps): React.ReactElement {
  const runQuery = useActiveRun(lineId);
  const { data: run, isLoading } = runQuery;
  const me = useAuthStore((s) => s.user);
  const [editPlanOpen, setEditPlanOpen] = React.useState(false);
  const isGestor = me?.role === "GESTOR";

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="bg-bg-muted h-20 rounded motion-safe:animate-pulse-dot" />
      </Card>
    );
  }

  if (runQuery.isError) {
    return (
      <Card className="overflow-hidden p-0">
        <Notify.QueryError error={runQuery.error} onRetry={() => void runQuery.refetch()} />
      </Card>
    );
  }

  if (!run) {
    return (
      <Card className="overflow-hidden p-0">
        <Notify.Empty
          title="Sem produção em andamento"
          description="Inicie uma execução pela barra de comandos acima para registrar produção, perdas e tempo parado."
          variant="card"
        />
      </Card>
    );
  }

  const progress =
    run.plannedQty > 0 ? Math.min(100, Math.round((run.producedQty / run.plannedQty) * 100)) : 0;

  const productNames = run.products.map((p) => p.product.name).filter(Boolean);
  const shortRunId = run.id.slice(0, 8);
  const statusLabel = run.status === "PAUSED" ? "Pausada" : "Em execução";
  const statusTone: React.ComponentProps<typeof Pill>["tone"] =
    run.status === "PAUSED" ? "warning" : "success";

  return (
    <Card className="space-y-4 rounded-2xl px-[22px] py-5" aria-labelledby="active-run-card-h2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              id="active-run-card-h2"
              className="text-[11px] uppercase tracking-wider text-text-tertiary"
            >
              Produto em produção
            </span>
            <span className="font-mono text-[11px] text-text-tertiary">RUN #{shortRunId}</span>
            <Pill tone={statusTone} withDot>
              {statusLabel}
            </Pill>
          </div>
          <h3 className="mt-1 truncate text-[16px] font-semibold text-text-primary">
            {productNames.length > 0 ? productNames.join(", ") : "Sem produtos"}
          </h3>
          <p className="mt-0.5 text-[12px] text-text-secondary">
            Início {formatRelativeTime(run.startedAt)}
            {run.expectedEnd ? ` · Fim planejado ${formatRelativeTime(run.expectedEnd)}` : null}
            {run.responsible ? ` · ${run.responsible}` : null}
          </p>

          {}
          <PlanDatesGrid
            plannedStartAt={run.plannedStartAt ?? null}
            startedAt={run.startedAt}
            expectedEnd={run.expectedEnd ?? null}
            completedAt={run.completedAt ?? null}
          />

          {}
          {isGestor && run.status !== "COMPLETED" && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-7 px-2 text-[12px]"
              onClick={() => setEditPlanOpen(true)}
              data-testid="active-run-edit-plan"
            >
              Editar plano
            </Button>
          )}
        </div>
      </div>

      {/* Spec 084 — a execução É a batelada: código e características efetivas. */}
      <BatchStrip lineId={lineId} runId={run.id} />

      {editPlanOpen && (
        <EditPlannedTimesDialog
          lineId={lineId}
          runId={run.id}
          open={editPlanOpen}
          initialPlannedStartAt={run.plannedStartAt}
          initialExpectedEnd={run.expectedEnd}
          onClose={() => setEditPlanOpen(false)}
        />
      )}

      <div className="space-y-1.5">
        <div className="flex justify-between text-[12px] text-text-secondary">
          <span>
            Produzido{" "}
            <span className="font-mono text-text-primary">{getQuantityLabel(run.producedQty)}</span>{" "}
            / <span className="font-mono">{getQuantityLabel(run.plannedQty)}</span>
          </span>
          <span className="font-mono font-medium text-text-primary">{progress}%</span>
        </div>
        <div
          className="h-2 overflow-hidden rounded-full bg-bg-muted-rd"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Progresso da run: ${progress}%`}
        >
          <div
            className="h-full w-[var(--bar-w)] bg-brand-500 transition-all"
            style={{ "--bar-w": `${progress}%` } as React.CSSProperties}
          />
        </div>
      </div>

      {}
      {}
      <OperationalEntryDisclosure
        lineId={lineId}
        activeRun={run}
        triggerLabel="Registrar dado"
        variant="buttons"
      />
    </Card>
  );
}
